

























import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { CalendarData, Properties } from "../types";
import moment from "moment";

@Component
export default class Dial extends Vue {
    @Prop() properties!: Properties;
    @Prop() now!: moment.Moment;
    @Prop() translations!: any;
    @Prop() styles!: string;
    @Prop() currentMeeting!: CalendarData | null;
    @Prop() isBookingDisabled!: boolean;
    @Prop() timeUntilNextMeeting!: number;
    @Prop() timeUntilRoomReleased!: number;

    // Properties
    get warningTime() {
        return this.properties.orangeTime;
    }

    get alertTime() {
        return this.properties.redTime;
    }

    dialColor = "#6facb7";
    normalColor = "#00A651";
    warningColor = "#F7941E";
    alertColor = "#ED1C24";
    interval = 0;
    timeout = 0;
    time = 0;

    @Watch("timeUntilNextMeeting") t() {
        if (this.timeUntilNextMeeting) {
            this.renderInnerCircleDial(360 - (6 * this.timeUntilNextMeeting), this.normalColor);
        } else {
            if (!this.currentMeeting && !this.timeUntilRoomReleased) this.stopDial();
        }
    }

    @Watch("timeUntilRoomReleased") m() {
        if (this.timeUntilRoomReleased) {
            this.renderInnerCircleDial(360 - (6 * this.timeUntilRoomReleased), this.alertColor);
        } else {
            if (!this.currentMeeting) this.stopDial();
        }
    }

    mounted() {
        this.stopDial();
    }

    beforeDestroy() {
        clearInterval(this.interval);
        clearTimeout(this.timeout);
    }

    startDial(endTime: moment.Moment) {
        console.log("Starting dial");
        clearInterval(this.interval);
        clearTimeout(this.timeout);
        this.time = moment.duration(moment(endTime).startOf("minute").diff(moment().startOf("minute"))).asMinutes();
        const proportion = 360 / this.time;
        let iterations = 1;
        this.renderDial(0);

        const maxIterations = this.time;

        const timeUntilNextMinute = 60 - moment().seconds();
        this.timeout = setTimeout(() => {
            this.time -= 1;
            this.renderDial(proportion * iterations);
            iterations++;
            if (iterations === maxIterations + 1) {
                this.stopDial();
            }
            this.interval = setInterval(() => {
                this.time -= 1;
                this.renderDial(proportion * iterations);
                iterations++;
                if (iterations === maxIterations + 1) {
                    this.stopDial();
                }
            }, 60000);
        }, timeUntilNextMinute * 1000);
    }

    renderInnerCircleDial(degree: number, color: string) {
        if (!this.$refs.dial) return;
        const canvas = this.$refs.dial as HTMLCanvasElement;
        const context = canvas.getContext("2d") as CanvasRenderingContext2D;
        context.save();
        context.clearRect(0, 0, canvas.width, canvas.height);
        this.drawMainDial(context, canvas.width, this.dialColor, 0);
        this.drawSmallDial(context, canvas.width, this.dialColor, 0);
        this.drawSmallDial(context, canvas.width, color, degree);
        this.drawDialPoints(context, canvas.width);
        context.restore();
    }

    stopDial() {
        console.log("Resetting dial");
        clearInterval(this.interval);
        clearTimeout(this.timeout);
        this.renderDial(-1);
        this.time = 0;
    }

    // Dial logic & Dial properties
    largeDialWidth = 20;
    smallDialWidth = 3;
    smallDialStartAngle = 0;
    smallDialRadians = 2 * Math.PI;

    // Set degrees to -1 to omit the progression arch
    renderDial(degree: number) {
        if (!this.$refs.dial) return;

        console.log("Rendering dial with", degree, "degrees");
        let progressionColor = this.normalColor;
        if (this.time <= this.warningTime) progressionColor = this.warningColor;
        if (this.time <= this.alertTime) progressionColor = this.alertColor;

        const canvas = this.$refs.dial as HTMLCanvasElement;
        const context = canvas.getContext("2d") as CanvasRenderingContext2D;
        context.save();
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (this.time <= this.alertTime && degree !== -1) this.drawBackground(context, canvas.width);
        this.drawMainDial(context, canvas.width, this.dialColor, 0);
        if (degree === -1 || this.time > this.alertTime) this.drawSmallDial(context, canvas.width, this.dialColor, 0);
        else this.drawSmallDial(context, canvas.width, "#ffffff", 0);

        if (degree !== -1) this.drawMainDial(context, canvas.width, progressionColor, degree);
        this.drawDialPoints(context, canvas.width);
        context.restore();
    }

    drawMainDial(context: CanvasRenderingContext2D, canvasWidth: number, color: string, degrees: number) {
        context.beginPath();
        context.translate(0, 0);
        context.lineWidth = this.largeDialWidth;
        context.arc(
            canvasWidth / 2,
            canvasWidth / 2,
            (canvasWidth - this.largeDialWidth) / 2,
            Math.PI * 1.5,
            Math.PI * (this.transformRange(degrees, { max: 360, min: 0 }, { max: 1.5, min: -0.5 }) - 0.5),
            true
        );
        context.strokeStyle = color;
        context.stroke();
    }

    transformRange = (value: number, r1: any, r2: any) => {
        var scale = (r2.max - r2.min) / (r1.max - r1.min);
        return (value - r1.min) * scale;
    }

    drawSmallDial(context: CanvasRenderingContext2D, canvasWidth: number, strokeStyle: string, degrees: number) {
        context.beginPath();
        context.lineWidth = this.smallDialWidth;
        context.arc(
            canvasWidth / 2,
            canvasWidth / 2,
            canvasWidth / 2 - (this.largeDialWidth + this.smallDialWidth + 1),
            Math.PI * 1.5,
            Math.PI * (this.transformRange(degrees, { max: 360, min: 0 }, { max: 1.5, min: -0.5 }) - 0.5),
            true
        );
        context.strokeStyle = strokeStyle;
        context.stroke();
    }

    drawBackground(context: CanvasRenderingContext2D, canvasWidth: number) {
        context.beginPath();
        context.arc(
            canvasWidth / 2,
            canvasWidth / 2,
            80,
            0,
            2 * Math.PI,
            false);
        context.fillStyle = this.alertColor;
        context.fill();
    }

    drawDialPoints(context: CanvasRenderingContext2D, diameter: number) {
        context.translate(diameter / 2, diameter / 2);
        for (var num = 1; num < 9; num++) {
            var ang = (num * Math.PI) / 4;
            context.rotate(ang);
            context.translate(0, -(diameter / 2) * 0.87);
            context.fillStyle = "#FFFFFF";
            context.fillRect(0, 0, 2, 6);
            context.rotate(-ang);
            context.rotate(ang);
            context.translate(0, (diameter / 2) * 0.87);
            context.rotate(-ang);
        }
    }
}
